import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { toast } from "react-toastify";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { base_url } from "../utils/axiosConfig";

const VendorProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  const [product, setProduct] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("user_website") &&
    JSON.parse(localStorage.getItem("user_website"));

  // ✅ FETCH PRODUCT BY ID
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${base_url}v1/vendor/getOneVendorProductForWebsite/${id}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch product");
        }

        const data = await response.json();
        console.log(data);
        const productData = data?.data;

        if (!productData || !productData.isActive) {
          throw new Error("Product not available");
        }

        setProduct(productData);
        setSelectedVariation(productData.variations?.[0] || null);
        setSelectedImage(productData.images?.[0] || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const isOutOfStock = selectedVariation?.stock <= 0;

  const increaseQty = () => {
    if (quantity < selectedVariation?.stock) {
      setQuantity((prev) => prev + 1);
    }
  };

  const decreaseQty = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const handleAddToCart = useCallback(async () => {
    try {
      if (!token?.token) {
        toast.error("Please login first");
        return;
      }

      if (isOutOfStock) {
        toast.error("Product is out of stock");
        return;
      }

      const response = await fetch(
        `${base_url}product/addtoCart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.token}`,
          },
          body: JSON.stringify({
            productSource: "vendor",
            productId: product._id,
            variationId: selectedVariation._id,
            quantity,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to add to cart");
      }

      toast.success("Added to cart successfully");
      navigate("/cart");
    } catch (error) {
      toast.error(error.message);
    }
  }, [token, product, selectedVariation, quantity, isOutOfStock, navigate]);

  // ✅ LOADING STATE
  if (loading) {
    return (
      <div className="text-center py-24 text-lg font-semibold">
        Loading product...
      </div>
    );
  }

  // ✅ ERROR STATE
  if (error || !product) {
    return (
      <div className="text-center py-24 text-lg font-semibold text-red-600">
        {error || "Product not found"}
      </div>
    );
  }

  const priceDetails = selectedVariation?.price;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 bg-white p-8 rounded-lg shadow">
        {/* IMAGE SECTION */}
        <div>
          <div className="h-[450px] flex items-center justify-center border rounded-lg overflow-hidden">
            <img
              src={selectedImage}
              alt={product.productName}
              className="object-contain h-full w-full"
            />
          </div>

          <div className="flex gap-4 mt-4 overflow-x-auto">
            {product.images?.map((img, index) => (
              <img
                key={index}
                src={img}
                onClick={() => setSelectedImage(img)}
                className={`h-20 w-20 cursor-pointer border rounded-md object-contain ${
                  selectedImage === img ? "border-green-600" : "border-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

        {/* DETAILS SECTION */}
        <div className="flex flex-col gap-5">
          <h1 className="text-3xl font-bold">{product.productName}</h1>

          <p className="text-gray-600">{product.briefDescription}</p>

          <div className="text-sm text-gray-500">
            Brand: <span className="font-medium">{product.brandName}</span>
          </div>

          {product.prescriptionRequired && (
            <div className="text-red-600 font-semibold">
              Prescription Required
            </div>
          )}

          {/* PRICE */}
          <div>
            <div className="text-2xl font-bold">
              {getCurrencySymbol()}
              {convertPrice(priceDetails?.totalAmount)}
            </div>

            <div className="text-sm text-gray-500">
              Base: {getCurrencySymbol()}
              {convertPrice(priceDetails?.amount)} | GST (
              {priceDetails?.gstPercent}%): {getCurrencySymbol()}
              {convertPrice(priceDetails?.gstAmount)}
            </div>
          </div>

          {/* VARIATIONS */}
          <div>
            <h3 className="font-semibold mb-2">Pack Size</h3>
            <div className="flex gap-3 flex-wrap">
              {product.variations.map((variation) => (
                <button
                  key={variation._id}
                  onClick={() => {
                    setSelectedVariation(variation);
                    setQuantity(1);
                  }}
                  className={`px-4 py-2 border rounded-md ${
                    selectedVariation?._id === variation._id
                      ? "bg-green-600 text-white border-green-600"
                      : "border-gray-300"
                  }`}
                >
                  Pack of {variation.packSize}
                </button>
              ))}
            </div>
          </div>

          {/* STOCK */}
          <div>
            {isOutOfStock ? (
              <span className="text-red-600 font-medium">Out of Stock</span>
            ) : (
              <span className="text-green-600 font-medium">
                {selectedVariation.stock} available
              </span>
            )}
          </div>

          {/* QUANTITY */}
          {!isOutOfStock && (
            <div className="flex items-center gap-4">
              <button
                onClick={decreaseQty}
                className="px-3 py-1 border rounded"
              >
                -
              </button>
              <span>{quantity}</span>
              <button
                onClick={increaseQty}
                className="px-3 py-1 border rounded"
              >
                +
              </button>
            </div>
          )}

          {/* BENEFITS */}
          {product.benefits?.length > 0 && (
            <div>
              <h3 className="font-semibold">Benefits</h3>
              <ul className="list-disc pl-5 text-gray-700">
                {product.benefits.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ADD TO CART */}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`mt-4 h-12 rounded-md text-white font-semibold flex items-center justify-center gap-2 ${
              isOutOfStock
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            <FaShoppingCart />
            {isOutOfStock ? "Out of Stock" : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorProductDetails;
